import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnalyticsSummaryResponse } from '@/api/types';
import { fmtScore, scoreColor } from '@/utils/analytics';
import { DimensionFilterNotice } from '../components/DimensionFilterNotice';
import { DimensionLabel } from '../components/DimensionLabel';
import { useOuNameResolver } from '../hooks/useAnalyticsNames';
import { OUDrillCard, type OuScoreRow } from './resumen/OUDrillCard';
import { TopBotBehaviors } from './resumen/TopBotBehaviors';
import { OmissionsCard, type OmissionRow } from './resumen/OmissionsCard';

interface ResumenTabProps {
  apiSummary?: AnalyticsSummaryResponse;
  /** Estado de carga del resumen — controla el estado explícito (ver MOCK_FALLBACK.md). */
  isLoadingSummary?: boolean;
  /** Estado de error del resumen — controla el estado explícito (ver MOCK_FALLBACK.md). */
  isErrorSummary?: boolean;
  /** Dimensión seleccionada en el filtro; `null` cuando el reporte cubre todas. */
  activeDimensionName?: string | null;
}

const MEDAL_COLORS = ['#F59E0B', '#9CA3AF', '#CD7F32'];

/** Claves que rotulan la misma columna en el KPI, el encabezado y el `data-label` de la fila. */
const EVALUATIONS_KEY = 'analytics.kpi.evaluations';
const NO_OBSERVABLES_KEY = 'analytics.kpi.noObservables';

export function ResumenTab({
  apiSummary,
  isLoadingSummary = false,
  isErrorSummary = false,
  activeDimensionName = null,
}: ResumenTabProps) {
  const { t } = useTranslation();
  const { resolveOuName } = useOuNameResolver();

  // KPIs — 100% desde apiSummary, con los nombres de campo del contrato real de
  // GET /v1/analytics/summary. Antes se leían campos que el backend nunca envió
  // (`avgScore`, `totalEvaluations` con otro significado, `responseRate` inexistente) y los
  // cinco indicadores salían siempre en cero.
  const kpiList = useMemo(() => {
    const avgScore = apiSummary?.overallAverage ?? 0;
    const totalEvaluations = apiSummary?.totalEvaluations ?? 0;
    const responseRate = apiSummary?.responseRate ?? 0;
    const avgPerDay = apiSummary?.avgPerDay ?? 0;
    const uniqueEvaluators = apiSummary?.uniqueEvaluators ?? 0;
    const totalParticipants = apiSummary?.totalParticipants ?? 0;

    return [
      {
        label: t(EVALUATIONS_KEY),
        value: totalEvaluations.toLocaleString(),
        sub: t('analytics.kpi.evaluatedPeople', { count: totalParticipants }),
        icon: 'fa-poll',
        color: '#1D4ED8',
        bg: '#EFF6FF',
        valueColor: undefined as string | undefined,
      },
      {
        label: t('analytics.kpi.avgScore'),
        value: fmtScore(avgScore),
        sub: t('analytics.kpi.scale'),
        icon: 'fa-star',
        color: 'var(--cp-success)',
        bg: 'var(--cp-success-bg)',
        valueColor: avgScore > 0 ? scoreColor(avgScore) : undefined,
      },
      {
        label: t('analytics.kpi.responseRate'),
        value: `${responseRate}%`,
        sub: t('analytics.kpi.responseRateSub'),
        icon: 'fa-percentage',
        color: '#D97706',
        bg: '#FEF3C7',
        valueColor: undefined as string | undefined,
      },
      {
        label: t('analytics.kpi.avgPerDay'),
        value: fmtScore(avgPerDay),
        sub: t('analytics.kpi.perDay'),
        icon: 'fa-paper-plane',
        color: '#0369A1',
        bg: '#F0F9FF',
        valueColor: undefined as string | undefined,
      },
      {
        label: t('analytics.kpi.evaluators'),
        value: uniqueEvaluators.toLocaleString(),
        sub: t('analytics.kpi.activeParticipants'),
        icon: 'fa-users',
        color: '#BE185D',
        bg: '#FDF2F8',
        valueColor: undefined as string | undefined,
      },
    ];
  }, [apiSummary, t]);

  // Ranking por dimensión de reporte: comportamiento donde el fundamental los tenga
  // configurados, fundamental donde no. Solo entran las dimensiones con evaluaciones: una
  // dimensión sin datos tiene puntaje 0 y ocuparía el último lugar como si fuera la peor.
  const rankedDimensions = useMemo(
    () => (apiSummary?.dimensions ?? [])
      .filter((dimension) => dimension.evaluations > 0)
      .sort((first, second) => second.score - first.score),
    [apiSummary],
  );

  const best = rankedDimensions[0];
  const worst = rankedDimensions[rankedDimensions.length - 1];
  const mostNoObs = useMemo(
    () => [...(apiSummary?.dimensions ?? [])]
      .sort((first, second) => second.noObs - first.noObs)[0],
    [apiSummary],
  );

  const stateMessage = useMemo(() => {
    if (isLoadingSummary) return t('common.loading');
    if (isErrorSummary) return t('common.error');
    if (rankedDimensions.length === 0) return t('analytics.noData');
    return null;
  }, [isLoadingSummary, isErrorSummary, rankedDimensions, t]);

  // Tasa de omisión por unidad, para poder mostrarla junto al score sin pedir otro endpoint.
  const omissionRateByOu = useMemo(
    () => new Map((apiSummary?.omissionsByUnit ?? []).map((unit) => [unit.ouId, unit.omissionRate])),
    [apiSummary],
  );

  const ouScores = useMemo<OuScoreRow[]>(
    () => (apiSummary?.scoreByOu ?? []).map((unit) => ({
      id: unit.ouId || '__sin-ou__',
      name: resolveOuName(unit.ouId),
      score: unit.score,
      evaluations: unit.evaluations,
      peopleCount: unit.participantCount,
      omissionRate: omissionRateByOu.get(unit.ouId) ?? null,
    })),
    [apiSummary, resolveOuName, omissionRateByOu],
  );

  const omissions = useMemo<OmissionRow[]>(
    () => (apiSummary?.omissionsByUnit ?? []).map((unit) => ({
      id: unit.ouId || '__sin-ou__',
      name: resolveOuName(unit.ouId),
      peopleCount: unit.peopleCount,
      evaluations: unit.evaluations,
      omittedCount: unit.omittedCount,
      omissionRate: unit.omissionRate,
    })),
    [apiSummary, resolveOuName],
  );

  return (
    <>
      <DimensionFilterNotice dimensionName={activeDimensionName} />

      <div className="au-kpis">
        {kpiList.map((kpi) => (
          <div key={kpi.label} className="au-kpi">
            <div className="au-kpi-icon" style={{ background: kpi.bg, color: kpi.color }}>
              <i className={`fas ${kpi.icon}`} aria-hidden="true" />
            </div>
            <div className="au-kpi-label">{kpi.label}</div>
            <div className="au-kpi-value" style={kpi.valueColor ? { color: kpi.valueColor } : {}}>
              {kpi.value}
            </div>
            <div className="au-kpi-sub">{kpi.sub}</div>
          </div>
        ))}
      </div>

      <div className="au-card">
        <div className="au-card-header">
          <div>
            <div className="au-card-title">
              <i className="fas fa-medal" aria-hidden="true" />
              {t('analytics.summary.fundPositioning')}
            </div>
            <div className="au-card-sub">{t('analytics.summary.behaviorPositioningDesc')}</div>
          </div>
        </div>

        {stateMessage && (
          <div className={`au-fund-state${isErrorSummary ? ' au-fund-state-error' : ''}`}>
            {isLoadingSummary && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
            {isErrorSummary && <i className="fas fa-triangle-exclamation" aria-hidden="true" />}
            {' '}{stateMessage}
          </div>
        )}

        {!stateMessage && (
          <>
            {best && worst && mostNoObs && (
              <div className="au-fund-highlights">
                <div className="au-fund-hl-card au-fund-hl-best">
                  <div className="au-fund-hl-icon au-fund-hl-icon-best">
                    <i className="fas fa-trophy" aria-hidden="true" />
                  </div>
                  <div className="au-fund-hl-body">
                    <div className="au-fund-hl-label">{t('analytics.summary.highestScore')}</div>
                    <div className="au-fund-hl-name">{best.name}</div>
                    {best.isBehavior && (
                      <div className="au-fund-hl-parent">{best.fundamentalName}</div>
                    )}
                  </div>
                  <div className="au-fund-hl-score" style={{ color: 'var(--cp-success)' }}>
                    {fmtScore(best.score)}
                  </div>
                </div>

                <div className="au-fund-hl-card au-fund-hl-worst">
                  <div className="au-fund-hl-icon au-fund-hl-icon-worst">
                    <i className="fas fa-arrow-down" aria-hidden="true" />
                  </div>
                  <div className="au-fund-hl-body">
                    <div className="au-fund-hl-label">{t('analytics.summary.lowestScore')}</div>
                    <div className="au-fund-hl-name">{worst.name}</div>
                    {worst.isBehavior && (
                      <div className="au-fund-hl-parent">{worst.fundamentalName}</div>
                    )}
                  </div>
                  <div className="au-fund-hl-score" style={{ color: 'var(--cp-danger)' }}>
                    {fmtScore(worst.score)}
                  </div>
                </div>

                <div className="au-fund-hl-card au-fund-hl-noobs">
                  <div className="au-fund-hl-icon au-fund-hl-icon-noobs">
                    <i className="fas fa-eye-slash" aria-hidden="true" />
                  </div>
                  <div className="au-fund-hl-body">
                    <div className="au-fund-hl-label">{t('analytics.summary.mostNoObs')}</div>
                    <div className="au-fund-hl-name">{mostNoObs.name}</div>
                    {mostNoObs.isBehavior && (
                      <div className="au-fund-hl-parent">{mostNoObs.fundamentalName}</div>
                    )}
                  </div>
                  <div className="au-fund-hl-score au-fund-hl-score-muted">
                    {mostNoObs.noObs}
                  </div>
                </div>
              </div>
            )}

            <div className="au-fund-rank-table">
              <div className="au-fund-rank-row au-fund-rank-header">
                <span className="au-frr-pos">#</span>
                <span className="au-frr-name">{t('analytics.summary.behaviorColumn')}</span>
                <span className="au-frr-score">{t('analytics.kpi.avgScore')}</span>
                <span className="au-frr-bar">{t('analytics.summary.distribution')}</span>
                <span className="au-frr-evals">{t(EVALUATIONS_KEY)}</span>
                <span className="au-frr-noobs">{t(NO_OBSERVABLES_KEY)}</span>
              </div>

              {rankedDimensions.map((dimension, index) => (
                <div
                  key={dimension.id}
                  className={[
                    'au-fund-rank-row',
                    index === 0 ? 'au-frr-best' : '',
                    index === rankedDimensions.length - 1 ? 'au-frr-worst' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <span className="au-frr-pos">
                    {index < 3
                      ? <i className="fas fa-medal" style={{ color: MEDAL_COLORS[index] }} aria-hidden="true" />
                      : index + 1}
                  </span>
                  <span className="au-frr-name">
                    <DimensionLabel
                      name={dimension.name}
                      isBehavior={dimension.isBehavior}
                      fundamentalName={dimension.fundamentalName}
                      color={dimension.color}
                    />
                  </span>
                  <span className="au-frr-score" style={{ color: scoreColor(dimension.score) }}>
                    {fmtScore(dimension.score)}
                  </span>
                  <span className="au-frr-bar">
                    <span className="au-frr-bar-track">
                      <span
                        className="au-frr-bar-fill"
                        style={{
                          width: `${(dimension.score / 4) * 100}%`,
                          background: dimension.color ?? 'var(--cp-green)',
                        }}
                      />
                    </span>
                  </span>
                  <span className="au-frr-evals" data-label={t(EVALUATIONS_KEY)}>
                    {dimension.evaluations.toLocaleString()}
                  </span>
                  <span
                    className="au-frr-noobs"
                    data-label={t(NO_OBSERVABLES_KEY)}
                  >
                    {dimension.noObs.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <OUDrillCard ouScores={ouScores} />

      <TopBotBehaviors
        topBehaviors={apiSummary?.topBehaviors ?? []}
        bottomBehaviors={apiSummary?.opportunities ?? []}
      />

      <OmissionsCard omissions={omissions} />
    </>
  );
}
